import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
}

function getEnvConfig(): FirebaseConfig | null {
  const apiKey = (import.meta.env.VITE_FIREBASE_API_KEY as string | undefined)?.trim()
  const authDomain = (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined)?.trim()
  const projectId = (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined)?.trim()
  const appId = (import.meta.env.VITE_FIREBASE_APP_ID as string | undefined)?.trim()
  if (!apiKey || !authDomain || !projectId || !appId) return null
  return { apiKey, authDomain, projectId, appId }
}

let app: FirebaseApp | null = null
let firestoreInstance: Firestore | null = null
let authInstance: Auth | null = null

/** Get or create Firebase app when config is present. */
export function getFirebaseApp(): FirebaseApp | null {
  if (app) return app
  const config = getEnvConfig()
  if (!config) return null
  app = getApps().length ? (getApp() as FirebaseApp) : initializeApp(config)
  return app
}

/** Whether the app should use Firestore (env says so and config is present). */
export function useFirestore(): boolean {
  const flag = import.meta.env.VITE_USE_FIRESTORE === 'true'
  const config = getEnvConfig()
  return Boolean(flag && config)
}

/** Get Firebase Auth instance. Returns null if Firebase config is missing. */
export function getFirebaseAuth(): Auth | null {
  if (authInstance) return authInstance
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) return null
  authInstance = getAuth(firebaseApp)
  return authInstance
}

/** Initialize Firebase app and return Firestore. Only call when useFirestore() is true. */
export function getFirestoreInstance(): Firestore {
  if (firestoreInstance) return firestoreInstance
  const config = getEnvConfig()
  if (!config) throw new Error('Firebase config missing; check .env and useFirestore()')
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) throw new Error('Firebase config missing; check .env')
  firestoreInstance = getFirestore(firebaseApp)
  return firestoreInstance
}
